import React from 'react';
import { Link } from 'react-router-dom';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { useUserConnections } from '@/hooks/useUserConnections';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, UserPlus } from 'lucide-react';
import ConnectionsIcon from '@/assets/icons/connections.svg';
import { useI18n } from '@/contexts/I18nContext';

interface UserConnectionsListProps {
  userId: string;
  userName?: string;
}

export const UserConnectionsList: React.FC<UserConnectionsListProps> = ({ userId, userName }) => {
  const { user } = useAuth();
  const { connections, loading, error } = useUserConnections(userId);
  const { t } = useI18n();

  if (loading) return <div className="text-center py-8">{t('connections.loading_connections')}</div>;
  if (error) return <div className="text-center text-red-500 py-8">{error}</div>;
  if (connections.length === 0) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      {/* Empty State Icon */}
      <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
        <img 
          src={ConnectionsIcon} 
          alt="Connections" 
          className="w-10 h-10 text-white/40"
          style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
        />
      </div>
      
      {/* Empty State Title */}
      <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
        {t('connections.no_connections_yet')}
      </h3>
      
      {/* Empty State Subtitle */}
      <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
        {t('connections.user_no_connections_yet')}
      </p>
    </div>
  );

  return (
    <div className="min-w-[320px] w-full overflow-hidden">
      {connections.map((profile, idx) => (
        <div
          key={profile.id}
          className="flex justify-between items-center px-0 py-3"
        >
          <div className="flex gap-2 flex-1 items-center min-w-0 mr-2">
            <Link to={`/profile/${profile.username}`} className="flex-shrink-0">
              <FramedAvatar 
                src={profile.profile_picture_url} 
                alt={profile.full_name} 
                initials={profile.full_name ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : ''} 
                size={42} 
                frameSize={{ w: 50, h: 55 }} 
                beltRank={profile.belt_rank} 
              />
            </Link>
            <div className="min-w-0 flex-1 overflow-hidden">
              <Link to={`/profile/${profile.username}`} className="font-semibold text-white text-ellipsis block hover:underline text-base">
                {profile.full_name}
              </Link>
              <Link to={`/profile/${profile.username}`} className="text-white/60 text-ellipsis block hover:underline" style={{ fontSize: '12px' }}>
                @{profile.username}
              </Link>
              {profile.academy && (
                <div className="text-[10px] text-white/60 text-ellipsis">{profile.academy}</div>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 ml-1">
            <ConnectionStatusButton profile={profile} currentUserId={user?.id} />
          </div>
        </div>
      ))}
    </div>
  );
};

interface ConnectionStatusButtonProps {
  profile: any;
  currentUserId?: string;
}

const ConnectionStatusButton: React.FC<ConnectionStatusButtonProps> = ({ profile, currentUserId }) => {
  const { status, loading, sendRequest } = useConnectionStatus(profile.id);
  const { t } = useI18n();
  const isOwnProfile = currentUserId === profile.id;

    if (isOwnProfile) return null;
    
    switch (status) {
      case 'connected':
        return (
        <div className="flex items-center justify-center gap-1 text-white text-[10px] bg-[#7F7F7F] px-2 py-1 rounded-full border border-white/20 whitespace-nowrap">
          <CheckCircle className="w-3 h-3 flex-shrink-0" />
            <span className="font-medium">{t('connection.connected')}</span>
          </div>
        );
      case 'pending':
        return (
        <div className="flex items-center gap-1 text-white text-[10px] bg-[#4A403E] px-2 py-1 rounded-full border border-white/20 whitespace-nowrap">
          <Clock className="w-3 h-3 flex-shrink-0" />
            <span className="font-medium">{t('connection.requested')}</span>
          </div>
        );
      case 'none':
        return (
          <button
            onClick={sendRequest}
            disabled={loading}
            className="flex items-center gap-1 text-white text-[10px] bg-[#D72E16] px-2 py-1 rounded-full border border-white/20 whitespace-nowrap hover:bg-[#D72E16]/90 transition-colors"
          >
            {loading ? (
              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <UserPlus className="w-3 h-3 flex-shrink-0" />
                <span className="font-medium">{t('connection.connect')}</span>
              </>
            )}
          </button>
        );
      default:
        return null;
    }
};